import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  startAttempt,
  submitAttempt,
  StartAttemptResponse,
  SubmitAttemptResult,
  SubmitAnswerItem,
} from '../services/studentScheduledTestService';

export default function StudentTestAttemptPage() {
  const { courseId, testId } = useParams<{ courseId: string; testId: string }>();
  const cId = Number(courseId);
  const tId = Number(testId);
  const navigate = useNavigate();

  const [phase, setPhase] = useState<'pre' | 'in-progress' | 'submitted'>('pre');
  const [attempt, setAttempt] = useState<StartAttemptResponse | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [result, setResult] = useState<SubmitAttemptResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await startAttempt(cId, tId);
      setAttempt(resp);
      setPhase('in-progress');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to start test';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!attempt) return;
    if (!window.confirm('Submit your answers? You cannot change them after submitting.')) return;

    setLoading(true);
    setError(null);
    try {
      const answerItems: SubmitAnswerItem[] = Object.entries(answers).map(([snapId, idx]) => ({
        snapshotQuestionId: Number(snapId),
        selectedOptionIndex: idx,
      }));
      const res = await submitAttempt(cId, tId, attempt.attemptId, answerItems);
      setResult(res);
      setPhase('submitted');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to submit';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (phase === 'submitted' && result) {
    const pct = result.scorePercent ?? 0;
    const color = pct >= 70 ? 'success' : pct >= 50 ? 'warning' : 'error';
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <CheckCircleIcon color={color as any} sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h4" gutterBottom>Test Submitted!</Typography>
        <Typography variant="h5" color={`${color}.main`} gutterBottom>
          {result.scoreCorrect} / {result.scoreTotal} ({pct}%)
        </Typography>
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => navigate(`/student/courses/${cId}/tests/${tId}/attempts`)}>
            View Attempt History
          </Button>
          <Button variant="contained" onClick={() => navigate(-1)}>
            Back to Tests
          </Button>
        </Box>
      </Container>
    );
  }

  if (phase === 'pre') {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Ready to start the test?</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Once started, please complete the test in one session.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button variant="contained" size="large" onClick={handleStart} disabled={loading}>
          {loading ? <CircularProgress size={22} /> : 'Start Test'}
        </Button>
      </Container>
    );
  }

  if (!attempt) return null;

  const answeredCount = Object.keys(answers).length;
  const totalCount = attempt.questions.length;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{attempt.testName}</Typography>
        <Chip
          label={`${answeredCount} / ${totalCount} answered`}
          color={answeredCount === totalCount ? 'success' : 'default'}
          variant="outlined"
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {attempt.questions.map((q, idx) => (
        <Box key={q.snapshotQuestionId} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {idx + 1}. {q.questionText}
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={answers[q.snapshotQuestionId] ?? ''}
              onChange={(e) =>
                setAnswers((prev) => ({
                  ...prev,
                  [q.snapshotQuestionId]: Number(e.target.value),
                }))
              }
            >
              {q.options.map((opt) => (
                <FormControlLabel
                  key={opt.index}
                  value={opt.index}
                  control={<Radio />}
                  label={opt.text}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <Divider sx={{ mt: 2 }} />
        </Box>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={22} /> : 'Submit Answers'}
        </Button>
      </Box>
    </Container>
  );
}
